import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import Button from '@lib/core/src/components/Button';
import TextInput from '@lib/core/src/components/TextInput';
import palette from '@lib/core/src/theme/palette';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { CircularProgress, Stack } from '@mui/material';
import SignUpVerifyError from '@pages/LoginFlow/SignUpVerifyError/signUpVerifyError';

import { AuthorDtoUserType, ChecklistDtoStatus, useVerifySignUp } from '@/api/generated';
import useLoginContext from '@/context/LoginFlowContextProvider/useLoginContext';
import RoutesMap from '@/enums/RoutesMap';
import { useCheckList } from '@/hooks/useCheckList';
import { checkListConfig } from '@/pages/HomePage/components/checklist/CheckListConfig';
import { generateEmptySpaces } from '@/utils/stringUtil';

import {
  Container,
  InputAndTitleContainer,
  InputContainer,
  InputError,
  InputsContainer,
  InputTitle,
  Title,
  TitleDescription,
  TitlesContainer,
  UserInfoCategoryContainer,
  UserInfoCategoryTitle,
  UserInfoCategoryValue,
  UserInfoContainer
} from './SignUpStyles';

const SignUp = () => {
  // Get search parameters from the URL
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { createCheckList } = useCheckList({ enabled: false });
  // State variables for password and confirmation password
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordError, setConfirmPasswordError] = useState('');
  const navigate = useNavigate();

  // Check if the form is valid
  const isValidForm =
    token &&
    !passwordError &&
    password &&
    !confirmPasswordError &&
    confirmPassword &&
    token.length > 0;

  const handlePasswordChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    const passwordRegex = /^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/;
    if (value && !passwordRegex.test(value)) {
      setPasswordError(
        'Password must be at least 8 characters long and include at least one letter and one number'
      );
    } else {
      setPasswordError('');
    }
  }, []);

  const handleConfirmPasswordChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setConfirmPassword(value);
      if (value !== password) {
        setConfirmPasswordError('Passwords do not match');
      } else {
        setConfirmPasswordError('');
      }
    },
    [password]
  );

  const toggleShowPassword = useCallback(() => {
    setShowPassword(prevShowPassword => !prevShowPassword);
  }, []);

  const toggleShowConfirmPassword = useCallback(() => {
    setShowConfirmPassword(prevShowConfirmPassword => !prevShowConfirmPassword);
  }, []);

  const {
    data,
    isLoading: verifyLoading,
    isSuccess: verifySuccess,
    isError: verifyError
  } = useVerifySignUp(
    { token: token! },
    {
      query: {
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false
      }
    }
  );

  const { signUp, signInLoading } = useLoginContext();

  const userInfo = useMemo(
    () => ({
      company:
        data?.items?.[0]?.type !== AuthorDtoUserType.jp ? data?.items?.[0]?.accounts?.[0].name : '',
      fullName: data ? `${data?.items?.[0]?.firstName} ${data?.items?.[0]?.lastName}` : '',
      email: data?.items?.[0]?.email
    }),
    [data]
  );

  const onSubmit = async () => {
    signUp({
      password: password,
      email: userInfo.email,
      registrationToken: token!
    }).then(() => {
      createCheckList(
        {
          data: {
            items: checkListConfig.map(({ title, status }) => ({ title, status })),
            status: ChecklistDtoStatus.inProgress
          }
        },
        {
          onSuccess: () => {
            navigate(RoutesMap.Home);
          }
        }
      );
    });
  };

  if (verifyLoading)
    return (
      <Stack>
        <CircularProgress sx={{ color: palette.common.color.grey2 }} />
      </Stack>
    );

  if (verifyError) return <SignUpVerifyError />;

  return (
    verifySuccess && (
      <Container>
        <TitlesContainer>
          <Title>Set your account</Title>
          <TitleDescription>Please pick a new password</TitleDescription>
        </TitlesContainer>
        <UserInfoContainer>
          <UserInfoCategoryContainer>
            <Stack direction="row">
              <UserInfoCategoryTitle>Company: {generateEmptySpaces(1)}</UserInfoCategoryTitle>
              <UserInfoCategoryValue>{userInfo.company}</UserInfoCategoryValue>
            </Stack>
            <Stack direction="row">
              <UserInfoCategoryTitle>Full Name: {generateEmptySpaces(1)}</UserInfoCategoryTitle>
              <UserInfoCategoryValue>{userInfo.fullName}</UserInfoCategoryValue>
            </Stack>
            <Stack direction="row">
              <UserInfoCategoryTitle>Email: {generateEmptySpaces(1)}</UserInfoCategoryTitle>
              <UserInfoCategoryValue>{userInfo.email}</UserInfoCategoryValue>
            </Stack>
          </UserInfoCategoryContainer>
        </UserInfoContainer>
        <InputsContainer>
          <InputAndTitleContainer>
            <InputTitle>Password</InputTitle>
            <Stack>
              <InputContainer>
                <TextInput
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Set password"
                  value={password}
                  onChange={handlePasswordChange}
                />
                <span
                  onClick={toggleShowPassword}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '8px',
                    zIndex: 15
                  }}
                >
                  {showPassword ? (
                    <VisibilityOffOutlinedIcon
                      sx={{ cursor: 'pointer', width: '14px', color: palette.common.color.grey1 }}
                    />
                  ) : (
                    <VisibilityOutlinedIcon
                      sx={{ cursor: 'pointer', width: '14px', color: palette.common.color.grey1 }}
                    />
                  )}
                </span>
              </InputContainer>
              {passwordError && <InputError>{passwordError}</InputError>}
            </Stack>
          </InputAndTitleContainer>
          <InputAndTitleContainer>
            <InputTitle>Confirm Password</InputTitle>
            <Stack>
              <InputContainer>
                <TextInput
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={handleConfirmPasswordChange}
                />
                <span
                  onClick={toggleShowConfirmPassword}
                  style={{
                    position: 'absolute',
                    right: '10px',
                    top: '8px',
                    zIndex: 15
                  }}
                >
                  {showPassword ? (
                    <VisibilityOffOutlinedIcon
                      sx={{ cursor: 'pointer', width: '14px', color: palette.common.color.grey1 }}
                    />
                  ) : (
                    <VisibilityOutlinedIcon
                      sx={{ cursor: 'pointer', width: '14px', color: palette.common.color.grey1 }}
                    />
                  )}
                </span>
              </InputContainer>
              {confirmPasswordError && !passwordError && (
                <InputError>{confirmPasswordError}</InputError>
              )}
            </Stack>
          </InputAndTitleContainer>
        </InputsContainer>
        <Stack>
          <Button
            text="Set password"
            onClick={() => onSubmit()}
            loading={signInLoading}
            disabled={!isValidForm}
          />
        </Stack>
      </Container>
    )
  );
};

export default SignUp;
